import { supabase } from '../clients/supabaseClient';

export interface PaymentHistoryEntry {
    id: string;
    payments_id: string;
    previous_status: string | null;
    current_status: string | null;
    reason: string | null;
    changed_by: string | null;
    created_at: string;
}

// Raw shape returned by the payments_history select below
interface PaymentHistoryRow {
    id: string;
    payments_id: string;
    previous: string | null;
    current: string | null;
    reason: string | null;
    changed_by: string | null;
    created_at: string;
}

// Raw shape returned by the statuses select below
interface StatusRow {
    id: string;
    name: string;
}

export async function fetchPaymentHistoryFromDb(orderId: string): Promise<PaymentHistoryEntry[]> {
    const { data, error } = await supabase
        .from('payments_history')
        .select('id, payments_id, previous, current, reason, changed_by, created_at')
        .eq('order_id', orderId)
        .eq('column', 'status_id')
        .order('created_at', { ascending: true });

    if (error) throw error;
    if (!data || data.length === 0) return [];

    // Collect all unique status UUIDs we need to resolve
    const uuids = new Set<string>();
    (data as PaymentHistoryRow[]).forEach((row) => {
        if (row.previous) uuids.add(row.previous);
        if (row.current)  uuids.add(row.current);
    });

    // Fetch all status names in one query
    const { data: statuses, error: statusError } = await supabase
        .from('statuses')
        .select('id, name')
        .in('id', Array.from(uuids));

    if (statusError) throw statusError;

    const statusMap: Record<string, string> = {};
    (statuses as StatusRow[] ?? []).forEach((s) => { statusMap[s.id] = s.name; });

    return (data as PaymentHistoryRow[]).map((row) => ({
        id:              row.id,
        payments_id:     row.payments_id,
        previous_status: row.previous ? (statusMap[row.previous] ?? null) : null,
        current_status:  row.current  ? (statusMap[row.current]  ?? null) : null,
        reason:          row.reason ?? null,
        changed_by:      row.changed_by ?? null,
        created_at:      row.created_at,
    }));
}